/**
 * P6 - the readiness page.
 *
 * Someone opens this because they are about to demo in ten minutes and want to
 * know whether it will work. So every failing row names the exact file and the
 * exact command that fixes it. There is no traceback anywhere on this page,
 * and no row that says only "error".
 */

import { useQuery } from '@tanstack/react-query'
import { Check, Minus, X } from 'lucide-react'

import { api } from '../api/client'
import { SitesPanel } from '../components/SitesPanel'

interface SelfCheck {
  name: string
  ok: boolean
  detail: string
  remedy: string
  group: string
  optional: boolean
}

interface SelfCheckPayload {
  ok: boolean
  checks: SelfCheck[]
  passed: number
  total: number
  failures: SelfCheck[]
  repo_root: string
  headline: string
}

const GROUP_LABELS: Record<string, string> = {
  runtime: 'Runtime',
  packages: 'Packages',
  data: 'Data files',
  ports: 'Ports',
  packaging: 'Packaging',
}

export default function Health() {
  const query = useQuery<SelfCheckPayload>({
    queryKey: ['selfcheck'],
    queryFn: () => api.raw('/selfcheck'),
    refetchInterval: 10_000,
  })

  const groups = (query.data?.checks ?? []).reduce<Record<string, SelfCheck[]>>(
    (acc, check) => {
      ;(acc[check.group] ??= []).push(check)
      return acc
    },
    {},
  )

  return (
    <div className="instrument h-full overflow-y-auto" style={{ background: 'var(--ink-bg)' }}>
      <div className="mx-auto max-w-[900px] px-4 py-4">
        <div className="label">Diagnostics</div>
        <h2 className="text-14 font-medium" style={{ color: 'var(--ink-text)' }}>
          Readiness check
        </h2>

        {query.isLoading ? (
          <div className="mt-4 space-y-2">
            <div className="skeleton h-12 w-full" />
            <div className="skeleton h-40 w-full" />
            <div className="skeleton h-32 w-full" />
          </div>
        ) : query.isError ? (
          <div className="empty-slot mt-4">
            <div className="empty-title">The API is not responding</div>
            <p className="mt-1">
              The readiness check runs on the backend, so this page cannot load until
              it is up. Start it with <span className="num">start.bat</span>, or from a
              terminal:
            </p>
            <p className="num mt-2" style={{ textAlign: 'left', color: 'var(--ink-text)' }}>
              python -m uvicorn backend.app.main:app --host 127.0.0.1 --port 8000
            </p>
          </div>
        ) : (
          <>
            <div
              className="mt-3 flex items-center gap-3 rounded p-3"
              style={{
                border: `1px solid ${query.data!.ok ? 'var(--ink-border)' : 'var(--ink-alert)'}`,
                background: 'var(--ink-surface)',
                borderRadius: 'var(--r-panel)',
              }}
            >
              {query.data!.ok ? (
                <Check className="h-4 w-4 shrink-0" style={{ color: 'var(--ink-ok)' }} />
              ) : (
                <X className="h-4 w-4 shrink-0" style={{ color: 'var(--ink-alert)' }} />
              )}
              <div className="min-w-0">
                <div style={{ color: 'var(--ink-text)' }}>{query.data!.headline}</div>
                <div className="num text-12" style={{ color: 'var(--ink-text-dim)', textAlign: 'left' }}>
                  {query.data!.passed}/{query.data!.total} required &middot; {query.data!.repo_root}
                </div>
              </div>
            </div>

            {Object.entries(groups).map(([group, checks]) => (
              <section key={group} className="mt-5">
                <div className="label mb-1">{GROUP_LABELS[group] ?? group}</div>
                <table className="grid-table">
                  <tbody>
                    {checks.map((check) => (
                      <tr key={check.name}>
                        <td style={{ width: 24 }}>
                          {check.ok ? (
                            <Check className="h-3.5 w-3.5" style={{ color: 'var(--ink-ok)' }} />
                          ) : check.optional ? (
                            <Minus className="h-3.5 w-3.5" style={{ color: 'var(--ink-text-faint)' }} />
                          ) : (
                            <X className="h-3.5 w-3.5" style={{ color: 'var(--ink-alert)' }} />
                          )}
                        </td>
                        <td style={{ width: 200, color: 'var(--ink-text)' }}>{check.name}</td>
                        <td
                          className="num"
                          style={{
                            textAlign: 'left',
                            color: check.ok ? 'var(--ink-text-dim)' : 'var(--ink-warn)',
                          }}
                        >
                          {check.detail}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {checks
                  .filter((c) => !c.ok && c.remedy)
                  .map((c) => (
                    <div
                      key={`${c.name}-remedy`}
                      className="mt-2 rounded p-2 text-12 leading-snug"
                      style={{
                        border: '1px solid var(--ink-border)',
                        background: 'var(--ink-surface)',
                        borderRadius: 'var(--r-control)',
                        color: 'var(--ink-text-dim)',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <span style={{ color: 'var(--ink-text)' }}>{c.name}: </span>
                      {c.remedy}
                    </div>
                  ))}
              </section>
            ))}
          </>
        )}

        {/* Outside the readiness branch on purpose: the site list has its own
            query, and must show even while the readiness check is still
            running or has failed. Inside it, a slow first check hid it. */}
        <SitesPanel />
      </div>
    </div>
  )
}
